import React from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Divider,
    Icon,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Typography
} from "@mui/material";
import { FieldGrid } from "components/FieldGrid";

const DesgloseTable = ({ data }) => {
    const { t } = useTranslation();

    return (
        <TableContainer component={Paper}>
        <Toolbar
	      sx={{
	        pl: { sm: 2 },
	        pr: { xs: 1, sm: 1 }	       
	      }}
	    >
	      
	        <Typography
	          sx={{ flex: '1 1 100%' }}
	          variant="h6"
	          id="tableTitle"
	          component="div"
	        >
	          {t('registreVerifactu.desglose.title')}
	        </Typography>
	    </Toolbar>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                    <TableRow>
                        <TableCell>{t('registreVerifactu.desglose.impuesto')}</TableCell>
                        <TableCell>{t('registreVerifactu.desglose.claveRegimen')}</TableCell>
                        <TableCell>{t('registreVerifactu.desglose.calificacionOperacion')}</TableCell>
                        <TableCell>{t('registreVerifactu.desglose.tipoImpositivo')}</TableCell>
                        <TableCell>{t('registreVerifactu.desglose.baseImponibleOimporteNoSujeto')}</TableCell>
                        <TableCell>{t('registreVerifactu.desglose.cuotaRepercutida')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data?.desgloses?.map((desglose, index) => (
                        <TableRow
                            key={index}
                            sx={{
                                "&:last-child td, &:last-child th": {
                                    border: 0,
                                },
                            }}
                        >
                            <TableCell>
                            	{t(
									`registreVerifactu.desglose.impostos.${desglose?.impuesto}`,
									{defaultValue: desglose?.impuesto }	
								)}
                            </TableCell>                   
                            <TableCell>
                            	{t(
									`registreVerifactu.desglose.clausRegim.${desglose?.claveRegimen}`,
									{defaultValue: desglose?.claveRegimen }	
								)}
                            </TableCell> 
                            <TableCell>
                                {desglose?.calificacionOperacion}
                            </TableCell>
                            <TableCell>
							    {desglose?.tipoImpositivo != null
							        ? `${desglose.tipoImpositivo}%`
							        : ""}
							</TableCell>
                            <TableCell>
                                {desglose?.baseImponibleOimporteNoSujeto}
                            </TableCell>
                            <TableCell>{desglose?.cuotaRepercutida}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const RegistreVerifactuForm = ({ data }) => {

    const [expanded, setExpanded] = React.useState(false);
	const { t } = useTranslation();
	
    const mainFields = [
        { key: t("registreVerifactu.numSerieFactura"), value: data?.numSerieFactura },
        {
		    key: t("registreVerifactu.fechaExpedicionFactura"),
		    value: formatLocalDate(data?.fechaExpedicionFactura),
		},
        { key: t("registreVerifactu.importeTotal"), value: data?.importeTotal },
        { key: t("registreVerifactu.enviamentDataEnviament"), value: formatDateTime(data?.enviamentDataEnviament) },
        { key: t("registreVerifactu.respostaEstadoRegistro"), value: data?.respostaEstadoRegistro },
        {
            key: t("registreVerifactu.respostaDescripcionError"),
            value: data?.respostaDescripcionError,
            ample: true,
        },
    ];

    const extraFields = [
        { key: t("registreVerifactu.operacion"), value: data?.operacion },
        { key: t("registreVerifactu.idVersion"), value: data?.idVersion },
        { key: t("registreVerifactu.idEmisorFactura"), value: data?.idEmisorFactura },
        { key: t("registreVerifactu.sifNumeroInstalacion"), value: data?.sifNumeroInstalacion },
        { key: t("registreVerifactu.secuencia"), value: data?.secuencia },
        {
            key: t("registreVerifactu.nombreRazonEmisor"),
            value: data?.nombreRazonEmisor,
            ample: true,
        },
        { key: t("registreVerifactu.tipoFactura"), value: data?.tipoFactura },
        {
            key: t("registreVerifactu.descripcionOperacion"),
            value: data?.descripcionOperacion,
            ample: true,
        },
        { key: t("registreVerifactu.cuotaTotal"), value: data?.cuotaTotal },
        { key: t("registreVerifactu.primerRegistro"), value: data?.primerRegistro },
        { key: t("registreVerifactu.tipoHuella"), value: data?.tipoHuella },
        { key: t("registreVerifactu.huella"), value: data?.huella, ample: true },
        { key: t("registreVerifactu.sifNombreRazon"), value: data?.sifNombreRazon, ample: true },
        { key: t("registreVerifactu.sifNif"), value: data?.sifNif },
        { key: t("registreVerifactu.sifNombre"), value: data?.sifNombre },
        { key: t("registreVerifactu.sifId"), value: data?.sifId },
        { key: t("registreVerifactu.sifVersion"), value: data?.sifVersion },
        {
            key: t("registreVerifactu.sifTipoUsoPosibleSoloVerifactu"),
            value: data?.sifTipoUsoPosibleSoloVerifactu,
        },
        {
            key: t("registreVerifactu.sifTipoUsoPosibleMultiOt"),
            value: data?.sifTipoUsoPosibleMultiOt,
        },
        {
            key: t("registreVerifactu.sifIndicadorMultiplesOT"),
            value: data?.sifIndicadorMultiplesOT,
        },
        {
		    key: t("registreVerifactu.recuperat"),
		    value: data?.recuperat ? "Sí" : "No",
		},
        { key: t("registreVerifactu.sifEmpresa"), value: data?.sifEmpresa?.description },
        { key: t("registreVerifactu.urlVerificacion"), value: data?.urlVerificacion, ample: true },
        { key: t("registreVerifactu.description"), value: data?.description, ample: true },
        { key: t("registreVerifactu.maxNumSerieId"), value: data?.maxNumSerieId },
        { key: t("registreVerifactu.createdBy"), value: data?.createdBy },
        {
		    key: t("registreVerifactu.createdDate"),
		    value: formatLocalDateTime(data?.createdDate),
		},
        {
		    key: t("registreVerifactu.httpRequestAvailable"),
		    value: data?.httpRequestAvailable ? "Sí" : "No",
		},
        { key: t("registreVerifactu.tipus"), value: data?.tipus },
		{
		    key: t("registreVerifactu.registroAnteriorFechaExpedicionFactura"),
		    value: formatLocalDate(data?.registroAnteriorFechaExpedicionFactura),
		},
    ];

    return (
        <Paper
            elevation={2}
            sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
        >
            <FieldGrid fields={mainFields} />

            <Button
                onClick={() => setExpanded(!expanded)}
                variant="outlined"
                sx={{ alignSelf: "flex-start", mt: 1, textTransform: "none" }}
                endIcon={
                    expanded ? (
                        <Icon>expand_less</Icon>
                    ) : (
                        <Icon>expand_more</Icon>
                    )
                }
            >
                {/* TODO: Traduir */}
                {expanded ? t('registreVerifactu.botoEncongir') : t('registreVerifactu.botoDesplegar')}
            </Button>

            {expanded && <FieldGrid fields={extraFields} />}
        </Paper>
    );
};

const RegistresVerifactuContent = ({ data }) => {
    return (
        <>
            <RegistreVerifactuForm data={data} />
            <Divider sx={{ my: 3 }} />
            <DesgloseTable data={data} />
        </>
    );
};

function formatDateTime(value) {
    if (!value) return "";

    return new Date(value)
        .toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        })
        .replace(",", "");
};

function formatLocalDate(date) {
    if (!Array.isArray(date) || date.length < 3) {
        return "";
    }

    const [year, month, day] = date;

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
};

function formatLocalDateTime(dateTime) {
    if (!Array.isArray(dateTime) || dateTime.length < 6) {
        return "";
    }

    const [year, month, day, hour, minute, second] = dateTime;

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
};

export default RegistresVerifactuContent;
